import { useCallback } from 'react'

import { cartIntermediateAccessorGetProductFamilyIds } from '../../../../cart-intermediate'
import {
  type AttributionAddToCartInput,
  attributionFindLatestMatchingItem,
} from '../../../../experiments/attribution'
import { recommendationAttributionRead } from '../../../../experiments/recommendation-attribution'
import { searchAttributionRead } from '../../../../experiments/search-attribution'
import { useGlobalConfigContext } from '../../../../global-config'
import {
  udpTrackingDataLayerPushAddToCart,
  udpTrackingImpressionStorageGet,
  udpTrackingPushRecommendationAddToCart,
  type UdpTrackingStoredImpression,
  useUdpTrackingBuildMetadata,
} from '../../../../udp-tracking'
import { useProductPageContext } from '../../productPageContext'
import { productPageUdpTrackingBuildProductAddToCart } from '../productPageUdpTrackingBuildProductAddToCart'

type ProductAttributionMatch = Pick<
  AttributionAddToCartInput,
  'productFamilyIds' | 'productId'
>

const getLatestProductAttributionImpression = (
  attributionMatch: ProductAttributionMatch,
): UdpTrackingStoredImpression | undefined => {
  const searchClick = attributionFindLatestMatchingItem(
    searchAttributionRead().clicks,
    attributionMatch,
  )
  const recommendationClick = attributionFindLatestMatchingItem(
    recommendationAttributionRead().clicks.filter(
      ({ interactionType }) => interactionType === 'product-click',
    ),
    attributionMatch,
  )

  if (
    recommendationClick &&
    (!searchClick || recommendationClick.createdAt >= searchClick.createdAt)
  ) {
    return udpTrackingImpressionStorageGet(
      recommendationClick.productId,
      'recommendation',
      recommendationClick.areaId,
    )
  }

  if (!searchClick) {
    return undefined
  }

  return udpTrackingImpressionStorageGet(
    searchClick.productId,
    searchClick.surface,
  )
}

export const useProductPageUdpTrackingAddToCart = (
  quantity?: number,
): (() => void) => {
  const buildMetadata = useUdpTrackingBuildMetadata()
  const globalConfig = useGlobalConfigContext()
  const {
    currentOffer,
    currentPrescriptionType,
    currentVariant,
    product,
    quantitySelectionControls: { quantity: selectedQuantity },
  } = useProductPageContext()

  return useCallback((): void => {
    const attributionMatch = {
      productFamilyIds: cartIntermediateAccessorGetProductFamilyIds({
        product,
      }),
      productId: currentVariant.upid,
    }

    const impression = getLatestProductAttributionImpression(attributionMatch)

    if (!impression || !currentOffer) {
      return
    }

    const trackedQuantity = quantity ?? selectedQuantity

    const productAddToCart = productPageUdpTrackingBuildProductAddToCart({
      currentOffer,
      currentPrescriptionType,
      currentVariant,
      globalConfig,
      isSponsored: impression.product.is_sponsored,
      quantity: trackedQuantity,
    })

    if (!productAddToCart) {
      return
    }

    if (impression.impression_origin === 'recommendation') {
      udpTrackingPushRecommendationAddToCart({
        addToCartSource: 'pdp',
        buildMetadata,
        impression,
        product: productAddToCart,
      })
    } else {
      udpTrackingDataLayerPushAddToCart({
        impressionId: impression.impression_id,
        impressionOrigin: impression.impression_origin,
        impressionOriginData: impression.impression_origin_data,
        metadata: buildMetadata(),
        product: productAddToCart,
      })
    }
  }, [
    buildMetadata,
    currentOffer,
    currentPrescriptionType,
    currentVariant,
    globalConfig,
    product,
    quantity,
    selectedQuantity,
  ])
}
